import React, { useState, useEffect, useRef } from 'react';
import axios from '@/utils/axiosConfig';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

const CompteClient = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Dialog states
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [showCreateTransaction, setShowCreateTransaction] = useState(false);
  const [showReconciliation, setShowReconciliation] = useState(false);

  // Form states
  const [accountForm, setAccountForm] = useState({
    client_id: '',
    account_name: '',
    account_number: '',
    opening_balance: 0,
    notes: ''
  });

  const [transactionForm, setTransactionForm] = useState({
    account_id: '',
    transaction_type: 'deposit',
    amount: 0,
    description: '',
    reference: '',
    notes: ''
  });

  const [reconciliationForm, setReconciliationForm] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    notes: ''
  });

  // Protection contre les mises à jour sur composant démonté
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      if (isMountedRef.current) setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch clients
      const clientsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        const clientList = clientsRes.data.filter(u => u.role === 'client');
        setClients(clientList);
      }

      // Fetch accounts
      const accountsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/trust-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) setAccounts(accountsRes.data);

      // Fetch transactions
      const transactionsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/trust-transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) setTransactions(transactionsRes.data);

      // Fetch reconciliations
      const reconciliationsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/trust-reconciliations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) setReconciliations(reconciliationsRes.data);

    } catch (error) {
      console.error('Error fetching data:', error);
      if (isMountedRef.current) toast.error('Erreur lors du chargement des données');
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/trust-accounts`,
        accountForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Compte en fiducie créé avec succès');
      setShowCreateAccount(false);
      setAccountForm({
        client_id: '',
        account_name: '',
        account_number: '',
        opening_balance: 0,
        notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating account:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    }
  };

  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/trust-transactions`,
        transactionForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Transaction créée avec succès');
      setShowCreateTransaction(false);
      setTransactionForm({
        account_id: '',
        transaction_type: 'deposit',
        amount: 0,
        description: '',
        reference: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating transaction:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la transaction');
    }
  };

  const handleCreateReconciliation = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/trust-reconciliation`,
        reconciliationForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Réconciliation mensuelle créée avec succès');
      setShowReconciliation(false);
      setReconciliationForm({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating reconciliation:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la réconciliation');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDownloadReconciliationPDF = async (reconciliationId, period) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/trust-reconciliations/${reconciliationId}/pdf`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement du PDF');
      }

      // Get filename from response headers or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `ETAT_RAPPROCHEMENT_${period}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('PDF téléchargé avec succès');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  const getTransactionBadge = (type) => {
    const badges = {
      deposit: <Badge className="bg-green-500">Dépôt</Badge>,
      withdrawal: <Badge className="bg-orange-500">Retrait</Badge>,
      fee: <Badge className="bg-blue-500">Honoraires</Badge>
    };
    return badges[type] || <Badge>{type}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Compte Client</h1>
            <p className="text-gray-600 mt-1">
              Gestion des comptes en fiducie et réconciliation mensuelle
            </p>
          </div>
          <div className="flex gap-2">
            <Dialog open={showCreateAccount} onOpenChange={setShowCreateAccount}>
              <DialogTrigger asChild>
                <Button>Nouveau Compte</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Créer un Compte en Fiducie</DialogTitle>
                  <DialogDescription>
                    Créer un nouveau compte en fiducie pour un client
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateAccount} className="space-y-4">
                  <div>
                    <Label>Client</Label>
                    <Select
                      value={accountForm.client_id}
                      onValueChange={(value) => {
                        const client = clients.find(c => c.id === value);
                        setAccountForm({
                          ...accountForm,
                          client_id: value,
                          account_name: `Compte en fiducie - ${client?.full_name || ''}`
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Nom du compte</Label>
                    <Input
                      value={accountForm.account_name}
                      onChange={(e) => setAccountForm({ ...accountForm, account_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Numéro de compte (optionnel)</Label>
                    <Input
                      value={accountForm.account_number}
                      onChange={(e) => setAccountForm({ ...accountForm, account_number: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Solde d'ouverture (CAD)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={accountForm.opening_balance}
                      onChange={(e) => setAccountForm({ ...accountForm, opening_balance: parseFloat(e.target.value) })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Input
                      value={accountForm.notes}
                      onChange={(e) => setAccountForm({ ...accountForm, notes: e.target.value })}
                    />
                  </div>
                  <Button type="submit" className="w-full">Créer le compte</Button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={showCreateTransaction} onOpenChange={setShowCreateTransaction}>
              <DialogTrigger asChild>
                <Button variant="outline">Nouvelle Transaction</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Créer une Transaction</DialogTitle>
                  <DialogDescription>
                    Ajouter une transaction à un compte en fiducie
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateTransaction} className="space-y-4">
                  <div>
                    <Label>Compte</Label>
                    <Select
                      value={transactionForm.account_id}
                      onValueChange={(value) => setTransactionForm({ ...transactionForm, account_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un compte" />
                      </SelectTrigger>
                      <SelectContent>
                        {accounts.filter(a => a.status === 'actif').map((account) => (
                          <SelectItem key={account.id} value={account.id}>
                            {account.account_name} - {formatCurrency(account.current_balance)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Type de transaction</Label>
                    <Select
                      value={transactionForm.transaction_type}
                      onValueChange={(value) => setTransactionForm({ ...transactionForm, transaction_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="deposit">Dépôt</SelectItem>
                        <SelectItem value="withdrawal">Retrait</SelectItem>
                        <SelectItem value="fee">Honoraires</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Montant (CAD)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={transactionForm.amount}
                      onChange={(e) => setTransactionForm({ ...transactionForm, amount: parseFloat(e.target.value) })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={transactionForm.description}
                      onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Référence (optionnel)</Label>
                    <Input
                      value={transactionForm.reference}
                      onChange={(e) => setTransactionForm({ ...transactionForm, reference: e.target.value })}
                      placeholder="CHQ-001, VIR-123, etc."
                    />
                  </div>
                  <Button type="submit" className="w-full">Créer la transaction</Button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={showReconciliation} onOpenChange={setShowReconciliation}>
              <DialogTrigger asChild>
                <Button variant="secondary">Réconciliation Mensuelle</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Réconciliation Mensuelle</DialogTitle>
                  <DialogDescription>
                    Générer un rapport de réconciliation pour un mois donné
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateReconciliation} className="space-y-4">
                  <div>
                    <Label>Mois</Label>
                    <Select
                      value={reconciliationForm.month.toString()}
                      onValueChange={(value) => setReconciliationForm({ ...reconciliationForm, month: parseInt(value) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                          <SelectItem key={month} value={month.toString()}>
                            {new Date(2000, month - 1).toLocaleString('fr-FR', { month: 'long' })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Année</Label>
                    <Input
                      type="number"
                      value={reconciliationForm.year}
                      onChange={(e) => setReconciliationForm({ ...reconciliationForm, year: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Input
                      value={reconciliationForm.notes}
                      onChange={(e) => setReconciliationForm({ ...reconciliationForm, notes: e.target.value })}
                    />
                  </div>
                  <Button type="submit" className="w-full">Générer la réconciliation</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Comptes Actifs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{accounts.filter(a => a.status === 'actif').length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Solde Total</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(accounts.reduce((sum, acc) => sum + acc.current_balance, 0))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Transactions ce mois</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {transactions.filter(t => {
                  const date = new Date(t.transaction_date);
                  const now = new Date();
                  return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                }).length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Réconciliations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{reconciliations.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="accounts" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="accounts">Comptes</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="reconciliations">Réconciliations</TabsTrigger>
          </TabsList>

          <TabsContent value="accounts" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Comptes en Fiducie</CardTitle>
                <CardDescription>
                  Liste de tous les comptes en fiducie des clients
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Nom du Compte</TableHead>
                      <TableHead>N° Compte</TableHead>
                      <TableHead>Solde Ouverture</TableHead>
                      <TableHead>Solde Actuel</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Date Création</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accounts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-gray-500">
                          Aucun compte en fiducie
                        </TableCell>
                      </TableRow>
                    ) : (
                      accounts.map((account) => (
                        <TableRow key={account.id}>
                          <TableCell className="font-medium">{account.client_name}</TableCell>
                          <TableCell>{account.account_name}</TableCell>
                          <TableCell>{account.account_number || '-'}</TableCell>
                          <TableCell>{formatCurrency(account.opening_balance)}</TableCell>
                          <TableCell className="font-bold text-green-600">
                            {formatCurrency(account.current_balance)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={account.status === 'actif' ? 'default' : 'secondary'}>
                              {account.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{formatDate(account.created_at)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transactions" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Transactions</CardTitle>
                <CardDescription>
                  Historique de toutes les transactions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Montant</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Référence</TableHead>
                      <TableHead>Solde Après</TableHead>
                      <TableHead>Créé Par</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-gray-500">
                          Aucune transaction
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell>{formatDate(transaction.transaction_date)}</TableCell>
                          <TableCell className="font-medium">{transaction.client_name}</TableCell>
                          <TableCell>{getTransactionBadge(transaction.transaction_type)}</TableCell>
                          <TableCell className={
                            transaction.transaction_type === 'deposit' 
                              ? 'text-green-600 font-bold' 
                              : 'text-red-600 font-bold'
                          }>
                            {transaction.transaction_type === 'deposit' ? '+' : '-'}
                            {formatCurrency(transaction.amount)}
                          </TableCell>
                          <TableCell>{transaction.description}</TableCell>
                          <TableCell>{transaction.reference || '-'}</TableCell>
                          <TableCell className="font-bold">
                            {formatCurrency(transaction.balance_after)}
                          </TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {transaction.created_by_name}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reconciliations" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Réconciliations Mensuelles</CardTitle>
                <CardDescription>
                  Rapports de réconciliation mensuels conformes à la section 9.1
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Période</TableHead>
                      <TableHead>Solde Ouverture</TableHead>
                      <TableHead>Dépôts</TableHead>
                      <TableHead>Retraits</TableHead>
                      <TableHead>Honoraires</TableHead>
                      <TableHead>Solde Clôture</TableHead>
                      <TableHead>Transactions</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Réconcilié Par</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reconciliations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center text-gray-500">
                          Aucune réconciliation
                        </TableCell>
                      </TableRow>
                    ) : (
                      reconciliations.map((recon) => (
                        <TableRow key={recon.id}>
                          <TableCell className="font-medium">{recon.period}</TableCell>
                          <TableCell>{formatCurrency(recon.opening_balance)}</TableCell>
                          <TableCell className="text-green-600">
                            {formatCurrency(recon.total_deposits)}
                          </TableCell>
                          <TableCell className="text-orange-600">
                            {formatCurrency(recon.total_withdrawals)}
                          </TableCell>
                          <TableCell className="text-blue-600">
                            {formatCurrency(recon.total_fees)}
                          </TableCell>
                          <TableCell className="font-bold text-green-600">
                            {formatCurrency(recon.closing_balance)}
                          </TableCell>
                          <TableCell>{recon.transaction_count}</TableCell>
                          <TableCell>
                            <Badge variant={recon.status === 'complete' ? 'default' : 'secondary'}>
                              {recon.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {recon.reconciled_by_name}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadReconciliationPDF(recon.id, recon.period)}
                            >
                              📄 Télécharger PDF
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default CompteClient;
